/**
 * Canonical IO Schema (Zod) for the Longship IO Ingestion Platform.
 *
 * Zod v3 schemas derived strictly from `docs/io-schema.md`.
 * Mirrors the Pydantic model in `packages/parser/schema/io_record.py` 1:1.
 *
 * Field names match the spec exactly:
 *   - Order-level and line-item fields use snake_case.
 *   - `_meta` and `_note` are JSON keys with a leading underscore.
 *
 * ESM module — package.json declares `"type": "module"`.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Regex constants
// ---------------------------------------------------------------------------

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Subset of ISO 4217 currency codes (extensible).
const ISO_4217_CURRENCIES = new Set([
  "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "SEK", "NZD",
  "MXN", "SGD", "HKD", "NOK", "KRW", "TRY", "INR", "BRL", "RUB", "ZAR",
  "AED", "SAR", "THB", "IDR", "MYR", "PHP", "PLN", "DKK", "CZK", "HUF",
  "ILS", "CLP", "COP", "PEN", "ARS", "VND", "EGP", "NGN", "PKR", "BDT",
]);

const ISO_3166_ALPHA2_RE = /^[A-Z]{2}$/;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Ad format enum — see io-schema.md § Ad Format Enum. */
const AdFormat = z.enum([
  "Display",
  "Video",
  "CTV",
  "Native",
  "Rich Media",
  "Audio",
  "Email",
  "DOOH",
]);

/** Pricing model enum — see io-schema.md § Pricing Model Enum.
 * CPCV (cost-per-completed-view) and CPV (cost-per-view) are distinct. */
const PricingModel = z.enum([
  "CPM",
  "CPC",
  "CPV",
  "CPCV",
  "CPA",
  "Flat Fee",
]);

const FlatFeePeriod = z.enum(["monthly", "weekly", "flight"]);

const FrequencyScope = z.enum(["user", "household"]);

// ---------------------------------------------------------------------------
// Nested schemas
// ---------------------------------------------------------------------------

/** Agency contact information. */
const AgencyContact = z.object({
  name: z.string().optional(),
  email: z.string().optional(),
});

/** Targeting object — see io-schema.md § Targeting Object. */
const Targeting = z.object({
  geo: z
    .array(
      z.string().refine(
        (v) => ISO_3166_ALPHA2_RE.test(v),
        "Invalid ISO 3166-1 alpha-2 country code"
      )
    )
    .optional(),
  geo_detail: z.string().optional(),
  audience: z.array(z.string()).optional(),
  intent_data: z.string().optional(),
  job_title: z.array(z.string()).optional(),
  device: z.array(z.string()).optional(),
  frequency_scope: FrequencyScope.optional(),
  daypart: z.string().optional(),
  brand_safety: z.string().optional(),
  placement: z.string().optional(),
  send_schedule: z.string().optional(),
});

/** IO line item / placement — see io-schema.md § Line Item Fields. */
const LineItem = z.object({
  line_item_id: z.string().optional(), // synthesized if absent
  line_item_name: z.string(),
  ad_format: AdFormat,
  pricing_model: PricingModel,
  rate: z.number().nonnegative().optional(),
  units: z.number().int().nonnegative().optional(),
  flat_fee_rate: z.number().nonnegative().optional(),
  flat_fee_period: FlatFeePeriod.optional(),
  flat_fee_months: z.number().int().positive().optional(),
  gross_budget: z.number().nonnegative(),
  net_budget: z.number().nonnegative().optional(),
  flight_start_date: z.string().refine(
    (v) => ISO_DATE_RE.test(v),
    "flight_start_date must be ISO 8601 format YYYY-MM-DD"
  ),
  flight_end_date: z.string().refine(
    (v) => ISO_DATE_RE.test(v),
    "flight_end_date must be ISO 8601 format YYYY-MM-DD"
  ),
  creative_dimensions: z.array(z.string()).optional(),
  creative_specs: z.string().optional(),
  targeting: Targeting.optional(),
  frequency_cap: z.string().nullable().optional(),
  third_party_tracking: z.array(z.string()).optional(),
  custom_kpis: z.array(z.string()).optional(),
  // `_note` is the line-item note (snake_case), distinct from `_meta.notes`.
  _note: z.string().optional(),
});

/** Extraction metadata — see io-schema.md § Confidence Scoring.
 * Optional with empty defaults — populated by the parser pipeline AFTER
 * the LLM call, not by the LLM itself.  Raw LLM output has no _meta field.
 */
const Meta = z.object({
  parsed_by: z.string().optional(),
  source_file: z.string().optional(),
  confidence: z.record(z.string(), z.number()).default({}),
  notes: z.string().optional(),
});

/** Canonical IO record — see io-schema.md § Order-Level Fields. */
const IORecord = z.object({
  io_number: z.string(),
  io_version: z.string().optional(),
  publisher: z.string(),
  agency: z.string(),
  agency_contact: AgencyContact.optional(),
  advertiser_name: z.string(),
  campaign_name: z.string().optional(),
  contract_start_date: z.string().refine(
    (v) => ISO_DATE_RE.test(v),
    "contract_start_date must be ISO 8601 format YYYY-MM-DD"
  ),
  contract_end_date: z.string().refine(
    (v) => ISO_DATE_RE.test(v),
    "contract_end_date must be ISO 8601 format YYYY-MM-DD"
  ),
  total_gross_budget: z.number().nonnegative(),
  total_net_budget: z.number().nonnegative().optional(),
  currency: z
    .string()
    .refine(
      (v) => ISO_4217_CURRENCIES.has(v),
      "Invalid ISO 4217 currency code"
    )
    .default("USD"),
  special_instructions: z.string().optional(),
  compliance: z.array(z.string()).optional(),
  line_items: z.array(LineItem),
  // `_meta` is parser-internal metadata (confidence, notes, source file).
  // It is attached by the parser pipeline AFTER the LLM call, not produced
  // by the LLM itself, so it is optional on the wire.
  _meta: Meta.optional(),
});

// ---------------------------------------------------------------------------
// Budget reconciliation (warning-only, non-blocking)
// ---------------------------------------------------------------------------

/**
 * Check budget reconciliation.
 *
 * Per io-schema.md § Budget Reconciliation Rule::
 *
 *   abs(sum(line_items[*].gross_budget) - total_gross_budget)
 *       <= 0.01 * total_gross_budget
 *
 * This is a WARNING-ONLY check — it does NOT block validation.
 * The SBM sample intentionally has a discrepancy that must surface
 * as a warning, not a validation error.  This mirrors the Pydantic
 * model's non-blocking `warnings.warn()` behaviour.
 *
 * @param {object} data - Parsed IO record (shape of `IORecord`).
 * @returns {{ ok: boolean, discrepancy: number, threshold: number, message?: string }}
 */
function checkBudgetReconciliation(data) {
  if (!data || !Array.isArray(data.line_items) || data.line_items.length === 0) {
    return { ok: true, discrepancy: 0, threshold: 0, message: "no line items" };
  }
  const lineSum = data.line_items.reduce(
    (sum, item) => sum + item.gross_budget,
    0
  );
  const discrepancy = Math.abs(lineSum - data.total_gross_budget);
  const threshold = 0.01 * data.total_gross_budget;
  if (discrepancy > threshold) {
    return {
      ok: false,
      discrepancy,
      threshold,
      message:
        "Budget reconciliation: sum of line item gross budgets (" +
        lineSum +
        ") differs from total_gross_budget (" +
        data.total_gross_budget +
        ") by " +
        discrepancy +
        " (threshold " +
        threshold +
        "). Review required.",
    };
  }
  return { ok: true, discrepancy, threshold };
}

export {
  AdFormat,
  PricingModel,
  FlatFeePeriod,
  FrequencyScope,
  AgencyContact,
  Targeting,
  LineItem,
  Meta,
  IORecord,
  checkBudgetReconciliation,
};
